import React, { FC, useMemo } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getAnswerItems, isTaskChecked } from '../../utils/TaskItemUtils';
import { isWalthroughTaskAnswered } from '../../utils/SiteSurveyUtils';

const getTaskAnswersFromWalkthrough = (walkthrough) => {
	const allTaskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	return allTaskAnswers.flatMap(getAnswerItems);
};

const getStatusColors = (checkedCount, totalCount) => {
	const diffPercentage = (checkedCount || 0) / (totalCount || 1);
	if (diffPercentage < 0.5) {
		return {
			color: '#FF4D4F',
			bgColor: '#f9e6e6',
		};
	} else if (diffPercentage < 1) {
		return {
			color: '#E59D0D',
			bgColor: 'rgb(252, 243, 217)',
		};
	} else {
		return {
			color: '#4B5563',
			bgColor: 'rgb(230, 249, 231)',
		};
	}
};

export const TaskListScoreDisplay: FC<any> = ({ checkedCount, totalCount }): React.ReactElement => {
	return (
		<div
			className="bodyregular"
			style={{ color: getStatusColors(checkedCount, totalCount).color }}
		>{`${checkedCount}/${totalCount}`}</div>
	);
};

export const GroupedTaskTaskListScoreDisplay: FC<any> = ({
	groupedAnswers,
}): React.ReactElement => {
	const allTaskListAnswers = useMemo(
		() => groupedAnswers.flatMap(getAnswerItems),
		[groupedAnswers]
	);
	const checkedCount = useMemo(
		() => allTaskListAnswers.filter(isTaskChecked).length,
		[allTaskListAnswers]
	);

	return allTaskListAnswers.length > 0 ? (
		<div className="flex flex-row items-center gap-2">
			<div className="h5regular text-[#4B5563]">Tasks:</div>
			<TaskListScoreDisplay checkedCount={checkedCount} totalCount={allTaskListAnswers.length} />
		</div>
	) : null;
};

export const GroupedTaskAnsweredDisplay: FC<any> = ({ groupedAnswers }): React.ReactElement => {
	const answeredCount = useMemo(
		() => groupedAnswers.filter(isWalthroughTaskAnswered).length,
		[groupedAnswers]
	);

	return groupedAnswers.length > 0 ? (
		<div className="flex flex-row items-center gap-2">
			<div className="h5regular text-[#4B5563]">Answered:</div>
			<TaskListScoreDisplay checkedCount={answeredCount} totalCount={groupedAnswers.length} />
		</div>
	) : null;
};

// Resolves the {checked, total} sub-item checkbox tally for the "Tasks: X/Y" pill. OpenSearch-backed
// rows don't ship the full task-answer items (taskAnswers is stripped for search), and the search-only
// answer arrays can't reconstruct an accurate checked/total — so the backend precomputes the metric,
// split by kind (checklistItems* and taskListItems*, mirroring getAnswerItems/isTaskChecked), which we
// sum here. Prefer those; DB-backed rows (e.g. the detail page) carry the full items, so count live.
const getTaskCompletionCounts = (walkthrough): { checkedCount: number; totalCount: number } => {
	// OpenSearch-backed rows carry the precomputed sub-item tallies split by kind — sum checklist +
	// task-list for the combined "Tasks: X/Y" pill. Use a direct presence check (not nullSafeGet, which
	// returns undefined for a legitimate 0 and so would never fall back to live counting for DB rows).
	const hasPrecomputedCounts =
		walkthrough.checklistItemsCount !== undefined || walkthrough.taskListItemsCount !== undefined;
	if (hasPrecomputedCounts) {
		return {
			checkedCount:
				nullSafeGetOrElse('checklistItemsCheckedCount', walkthrough, 0) +
				nullSafeGetOrElse('taskListItemsCheckedCount', walkthrough, 0),
			totalCount:
				nullSafeGetOrElse('checklistItemsCount', walkthrough, 0) +
				nullSafeGetOrElse('taskListItemsCount', walkthrough, 0),
		};
	}
	const items = getTaskAnswersFromWalkthrough(walkthrough);
	return { checkedCount: items.filter(isTaskChecked).length, totalCount: items.length };
};

export const WalkthroughTaskListScorePill: FC<any> = ({ walkthrough }): React.ReactElement => {
	const { checkedCount, totalCount } = useMemo(
		() => getTaskCompletionCounts(walkthrough),
		[walkthrough]
	);

	return totalCount > 0 ? (
		<div
			className="flex flex-row items-center gap-2"
			style={{
				background: getStatusColors(checkedCount, totalCount).bgColor,
				padding: '4px 12px',
				borderRadius: 16,
				textTransform: 'capitalize',
			}}
		>
			<div className="h5regular text-[#4B5563]">Tasks:</div>
			<TaskListScoreDisplay checkedCount={checkedCount} totalCount={totalCount} />
		</div>
	) : null;
};

const WalkthroughTaskListScoreDisplay: FC<any> = ({ walkthrough }): React.ReactElement => {
	const allTaskListAnswers = useMemo(
		() => getTaskAnswersFromWalkthrough(walkthrough),
		[walkthrough]
	);
	const checkedCount = useMemo(
		() => allTaskListAnswers.filter(isTaskChecked).length,
		[allTaskListAnswers]
	);

	return allTaskListAnswers.length > 0 ? (
		<TaskListScoreDisplay checkedCount={checkedCount} totalCount={allTaskListAnswers.length} />
	) : null;
};

export default WalkthroughTaskListScoreDisplay;
